import Animated, { FadeIn } from 'react-native-reanimated';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import type { Person } from '@/game/types';
import { Avatar } from '@/components/avatar';
import { SlotReel } from '@/components/slot-reel';
import { colors, radius, type } from '@/theme';

/** The reel clips to this, so the row can't grow with the drafted person. */
export const PERSON_FACE_HEIGHT = 48;

type Props = {
  person: Person;
  index: number;
  selected: boolean;
  onPress: () => void;
  /** Opens the "who is this?" card. A long press suppresses `onPress`. */
  onLongPress?: () => void;
  /** Faces the reel rushes past before landing on `person`. Omit to skip the spin. */
  decoys?: Person[];
  /** When this row's reel starts, measured from mount. */
  spinDelay?: number;
};

function PersonFace({ person, selected }: { person: Person; selected: boolean }) {
  return (
    <View style={styles.face}>
      <Avatar initials={person.initials} size={PERSON_FACE_HEIGHT} accent={selected ? colors.gold : null} />
      <View style={styles.copy}>
        <Text style={styles.name} numberOfLines={1} allowFontScaling={false}>
          {person.name}
        </Text>
        <Text style={styles.tagline} numberOfLines={1} allowFontScaling={false}>
          {person.tagline}
        </Text>
      </View>
    </View>
  );
}

export function PersonRow({
  person,
  index,
  selected,
  onPress,
  onLongPress,
  decoys,
  spinDelay = 0,
}: Props) {
  return (
    <Animated.View entering={FadeIn.delay(40 + index * 30).duration(200)}>
      <Pressable
        onPress={onPress}
        onLongPress={onLongPress}
        delayLongPress={350}
        style={({ pressed }) => [styles.row, selected && styles.rowSelected, pressed && styles.pressed]}>
        {decoys && decoys.length > 0 ? (
          <SlotReel
            style={styles.reel}
            height={PERSON_FACE_HEIGHT}
            delay={spinDelay}
            landed={<PersonFace person={person} selected={selected} />}
            decoys={decoys.map((decoy) => (
              <PersonFace key={decoy.id} person={decoy} selected={false} />
            ))}
          />
        ) : (
          <View style={styles.reel}>
            <PersonFace person={person} selected={selected} />
          </View>
        )}
        <Text style={[styles.arrow, selected && styles.arrowSelected]}>→</Text>
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    backgroundColor: colors.card,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.borderSoft,
    paddingHorizontal: 13,
    paddingVertical: 9,
  },
  rowSelected: {
    borderColor: colors.gold,
    backgroundColor: colors.cardHi,
  },
  pressed: {
    opacity: 0.8,
    transform: [{ scale: 0.99 }],
  },
  reel: {
    flex: 1,
  },
  face: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
  },
  copy: {
    flex: 1,
    gap: 2,
  },
  name: {
    ...type.title,
    fontSize: 19,
    color: colors.text,
  },
  tagline: {
    ...type.body,
    fontSize: 14,
    color: colors.dim,
  },
  arrow: {
    fontSize: 20,
    color: colors.faint,
  },
  arrowSelected: {
    color: colors.gold,
  },
});
